"use client"

import Image from "next/image"
import { useId } from "react"
import type { CSSProperties } from "react"

import { cn } from "./cn"

import frontProjection from "./unifi-g6-studio-projection.json"
import sideProjection from "./unifi-g6-studio-side-projection.json"
import type { UnifiG6TurretProps, UnifiSignalColor } from "./unifi-types"
import styles from "./UnifiG6Turret.module.css"
import studioStyles from "./UnifiG6Studio.module.css"
import white from "./assets/unifi-white.webp"
import black from "./assets/unifi-black.webp"
import clay from "./assets/unifi-clay.webp"
import whiteSide from "./assets/unifi-white-side.webp"
import blackSide from "./assets/unifi-black-side.webp"
import claySide from "./assets/unifi-clay-side.webp"

const BODIES = {
  white, black, clay, "white-side": whiteSide, "black-side": blackSide, "clay-side": claySide,
}

export type UnifiG6StudioProps = UnifiG6TurretProps & {
  /** Replace the body and optics with matte gray materials on the same geometry. */
  appearance?: "material" | "clay"
  /** Subtle green marketing cue on the lower face, independent of recording. */
  showActivityLed?: boolean
  /** Use a steady light in still artwork; demos can retain the gentle pulse. */
  activityLedMode?: "pulse" | "steady"
}

type ProjectedEllipse = {
  cx: number
  cy: number
  rx: number
  ry: number
  rotation?: number
}

const SIGNAL_COLORS: Record<UnifiSignalColor, string> = {
  purple: "#8073ef",
  blue: "#59a7f5",
  red: "#ef706e",
}

function ellipseProps(ellipse: ProjectedEllipse) {
  const {
    cx, cy, rx, ry, rotation = 0,
  } = ellipse
  return {
    cx, cy, rx, ry, transform: `rotate(${rotation} ${cx} ${cy})`,
  }
}

/** A rendered body with the same controlled, accessible interactions as the SVG study. */
export function UnifiG6Studio({
  finish = "white", view = "front", status = "idle", nightVision = false, showSignalRing = true,
  signalActive, signalColor = "purple", displayPaused = false, disabled = false, onPreview, className,
  appearance = "material", showActivityLed = false, activityLedMode = "pulse",
}: UnifiG6StudioProps) {
  const id = useId().replace(/:/g, "")
  const online = status !== "offline"
  const ringActive = showSignalRing && (signalActive ?? status === "recording")
  const activityLedActive = showActivityLed && online
  const isClay = appearance === "clay"
  const projection = view === "side" ? sideProjection : frontProjection
  const bodyName = `${isClay ? "clay" : finish}${view === "side" ? "-side" : ""}` as keyof typeof BODIES
  const finishDescription = isClay ? "gray clay study" : `${finish} finish`
  const description = `Ubiquiti G6 Turret, ${finishDescription}, studio ${view === "side" ? "three-quarter side" : "front"} view. ${status}. ${nightVision && online ? "Infrared night vision." : "Daylight optics."}${ringActive ? ` Illustrative ${signalColor} lens ring active.` : ""}${activityLedActive ? " Illustrative green activity light." : ""}`

  return (
    <div
      className={cn(styles.device, className)}
      data-unifi-device={finish}
      data-rendering="studio"
      data-appearance={appearance}
      data-view={view}
      data-status={status}
      data-activity-led={activityLedActive}
      data-paused={displayPaused}
      style={{
        "--camera-signal": SIGNAL_COLORS[signalColor],
        aspectRatio: `${projection.width} / ${projection.height}`,
      } as CSSProperties}
    >
      <svg
        className={cn(styles.art, studioStyles.shadow)}
        style={{ position: "absolute", inset: 0 }}
        viewBox={`0 0 ${projection.width} ${projection.height}`}
        aria-hidden="true"
      >
        <defs>
          <filter
            id={`${id}-ground-shadow`}
            x="-30%"
            y="-150%"
            width="160%"
            height="400%"
          >
            <feGaussianBlur stdDeviation="18" />
          </filter>
          <filter
            id={`${id}-contact-shadow`}
            x="-30%"
            y="-150%"
            width="160%"
            height="400%"
          >
            <feGaussianBlur stdDeviation="8" />
          </filter>
        </defs>
        <ellipse
          {...ellipseProps(projection.shadow)}
          fill="#26303a"
          opacity="0.16"
          filter={`url(#${id}-ground-shadow)`}
        />
        <ellipse
          cx={projection.shadow.cx}
          cy={projection.shadow.cy}
          rx={projection.shadow.rx * 0.82}
          ry={projection.shadow.ry * 0.28}
          fill="#151d25"
          opacity="0.1"
          filter={`url(#${id}-contact-shadow)`}
        />
      </svg>
      <Image
        className={studioStyles.body}
        src={BODIES[bodyName]}
        alt=""
        width={projection.width}
        height={projection.height}
        unoptimized
        draggable={false}
      />
      <svg
        className={cn(styles.art, studioStyles.overlay)}
        style={{ position: "absolute", inset: 0 }}
        viewBox={`0 0 ${projection.width} ${projection.height}`}
        role="img"
        aria-label={description}
      >
        <defs>
          <filter
            id={`${id}-signal-glow`}
            x="-60%"
            y="-60%"
            width="220%"
            height="220%"
          >
            <feGaussianBlur stdDeviation="5" />
          </filter>
          <filter
            id={`${id}-activity-glow`}
            x="-100%"
            y="-100%"
            width="300%"
            height="300%"
          >
            <feGaussianBlur stdDeviation="7" />
          </filter>
        </defs>
        <g
          className={styles.infrared}
          data-active={!isClay && nightVision && online}
        >
          {projection.infrared.map((emitter, index) => (
            <ellipse
              key={index}
              {...ellipseProps(emitter)}
            />
          ))}
        </g>
        <ellipse
          {...ellipseProps(projection.statusLed)}
          className={styles.statusLed}
          data-online={!isClay && online}
        />
        {activityLedActive && (
          <g
            className={studioStyles.activityLed}
            data-camera-activity-led
            data-mode={activityLedMode}
          >
            <ellipse
              {...ellipseProps({
                ...projection.activityLed,
                rx: projection.activityLed.rx * 2.5,
                ry: projection.activityLed.ry * 2.5,
              })}
              fill="#62c48c"
              opacity="0.35"
              filter={`url(#${id}-activity-glow)`}
            />
            <ellipse
              {...ellipseProps(projection.activityLed)}
              fill="#55b782"
            />
            <ellipse
              {...ellipseProps({
                ...projection.activityLed,
                rx: projection.activityLed.rx * 0.35,
                ry: projection.activityLed.ry * 0.35,
              })}
              fill="#d6f9e3"
              opacity="0.8"
            />
          </g>
        )}
        <g
          className={cn(styles.signal, isClay && studioStyles.claySignal)}
          data-active={ringActive}
        >
          <ellipse
            {...ellipseProps(projection.lens)}
            className={styles.signalGlow}
            filter={`url(#${id}-signal-glow)`}
          />
          <ellipse
            {...ellipseProps(projection.lens)}
            className={styles.signalTrack}
          />
          <ellipse
            {...ellipseProps(projection.lens)}
            pathLength="100"
            className={styles.signalTrace}
          />
        </g>
      </svg>
      {onPreview && (
        <button
          type="button"
          className={styles.previewButton}
          aria-label={`Open ${isClay ? "gray clay" : finish} camera preview`}
          disabled={disabled || !online || status === "connecting"}
          onClick={onPreview}
        />
      )}
    </div>
  )
}
